import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiImage, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import teamService from '../../services/teamService';
import { CreateTeamMemberData } from '../../types/team.types';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import Card from '../../components/common/Card/Card';
import styles from './CreateTeam.module.css';

interface FormData {
  name: string;
  email: string;
  userID: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  role: string;
  profilePhoto: File | null;
}

const CreateTeam: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    userID: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: 'blogger',
    profilePhoto: null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are allowed');
        return;
      }

      setFormData((prev) => ({ ...prev, profilePhoto: file }));

      // Generate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      console.log('✅ Profile photo selected:', file.name);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, profilePhoto: null }));
    setImagePreview('');
  };

  const validateForm = (): boolean => {
    // Name validation
    if (!formData.name.trim() || formData.name.length < 2 || formData.name.length > 50) {
      toast.error('Name must be between 2 and 50 characters');
      return false;
    }

    // Email validation
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please provide a valid email address');
      return false;
    }

    // User ID validation
    if (formData.userID.length < 4 || formData.userID.length > 20) {
      toast.error('User ID must be between 4 and 20 characters');
      return false;
    }

    const userIDRegex = /^[A-Z0-9_]+$/;
    if (!userIDRegex.test(formData.userID.toUpperCase())) {
      toast.error('User ID can only contain uppercase letters, numbers, and underscores');
      return false;
    }

    // Mobile validation
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(formData.mobile)) {
      toast.error('Please provide a valid 10-digit Indian mobile number starting with 6-9');
      return false;
    }

    // Password validation
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(formData.password)) {
      toast.error('Password must contain uppercase, lowercase, number, and special character');
      return false;
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      console.log('📝 Creating team member...');

      const submitData: CreateTeamMemberData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        userID: formData.userID.trim().toUpperCase(),
        mobile: formData.mobile.trim(),
        password: formData.password,
        role: formData.role as any,
      };

      if (formData.profilePhoto) {
        submitData.profilePhoto = formData.profilePhoto;
      }

      await teamService.createTeamMember(submitData);

      toast.success('Team member created successfully!');
      navigate('/team');
    } catch (error: any) {
      console.error('❌ Failed to create team member:', error);

      let errorMessage = 'Failed to create team member';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errorMessage = errors.map((e: any) => e.message).join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.values(errors).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (formData.name || formData.email || formData.userID) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    navigate('/team');
  };

  return (
    <div className={styles.createTeam}>
      <div className={styles.header}>
        <Button variant="ghost" icon={<FiArrowLeft />} onClick={handleCancel}>
          Back to Team
        </Button>
        <h1 className={styles.title}>Add New Team Member</h1>
        <p className={styles.subtitle}>Create a new user account with specific role and permissions</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Profile Photo */}
        <Card title="Profile Photo (Optional)" className={styles.section}>
          <div className={styles.imageUpload}>
            {imagePreview ? (
              <div className={styles.imagePreviewContainer}>
                <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                <button type="button" className={styles.removeImage} onClick={removeImage}>
                  <FiX /> Remove
                </button>
              </div>
            ) : (
              <label className={styles.imageUploadLabel}>
                <FiImage className={styles.uploadIcon} />
                <p className={styles.uploadText}>Click to upload profile photo</p>
                <p className={styles.uploadHint}>PNG, JPG, WebP up to 5MB</p>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  className={styles.fileInput}
                />
              </label>
            )}
          </div>
        </Card>

        {/* Basic Information */}
        <Card title="Basic Information" className={styles.section}>
          <div className={styles.formGrid}>
            <div className={styles.fullWidth}>
              <Input
                name="name"
                label="Full Name *"
                placeholder="Enter full name (2-50 characters)"
                value={formData.name}
                onChange={handleInputChange}
                required
                fullWidth
                maxLength={50}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  name="email"
                  type="email"
                  label="Email Address *"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </div>

              <div className={styles.formGroup}>
                <Input
                  name="mobile"
                  type="tel"
                  label="Mobile Number *"
                  placeholder="10-digit number (6-9 start)"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  maxLength={10}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  name="userID"
                  label="User ID *"
                  placeholder="UPPERCASE (4-20 chars)"
                  value={formData.userID}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  maxLength={20}
                />
                <p className={styles.hint}>Uppercase letters, numbers, and underscores only</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                >
                  <option value="blogger">Blogger</option>
                  <option value="editor">Editor</option>
                  <option value="manager">Manager</option>
                  <option value="executive">Executive</option>
                  <option value="developer">Developer</option>
                  <option value="affiliater">Affiliater</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </Card>

        {/* Security */}
        <Card title="Security" className={styles.section}>
          <div className={styles.formGrid}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Password *"
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
                <p className={styles.hint}>
                  Must contain: uppercase, lowercase, number, and special character
                </p>
              </div>

              <div className={styles.formGroup}>
                <Input
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  label="Confirm Password *"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                <span>Show passwords</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>

          <Button type="submit" variant="primary" icon={<FiSave />} loading={loading} disabled={loading}>
            {loading ? 'Creating...' : 'Create Team Member'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateTeam;